import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, X, Lock, Clock3, CheckCircle2, Eye, FileDown } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const STATUS_UI = {
  active: { label: "Active", cls: "bg-green-100 text-green-700", Icon: CheckCircle2 },
  locked: { label: "Locked", cls: "bg-red-100 text-red-600", Icon: Lock },
  temporary: { label: "Temporary Access", cls: "bg-amber-100 text-amber-700", Icon: Clock3 },
};

export const fmtGrace = (iso) => {
  try {
    return new Date(iso).toLocaleString("en-AU", { day: "numeric", month: "short", hour: "numeric", minute: "2-digit" });
  } catch {
    return iso;
  }
};

const Row = ({ k, v }) => (
  <div className="flex items-start justify-between gap-4 border-b border-line/60 py-2 text-sm">
    <span className="shrink-0 text-slate-500">{k}</span>
    <span className="text-right font-semibold text-slate-900">{v || "—"}</span>
  </div>
);

export const WorkerDetailModal = ({ workerId, onClose }) => {
  const [d, setD] = useState(null);
  useEffect(() => {
    axios.get(`${API}/workers/${workerId}`).then(({ data }) => setD(data)).catch(() => toast.error("Could not load worker details."));
  }, [workerId]);
  const p = d?.profile || {};
  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/60 sm:items-center" data-testid="worker-detail-modal">
      <div className="flex max-h-[88vh] w-full max-w-lg flex-col rounded-t-3xl bg-white sm:rounded-3xl">
        <div className="flex items-center justify-between border-b border-line p-4">
          <p className="font-display text-lg font-bold text-slate-900">{d?.name || "Worker"} — Details</p>
          <button onClick={onClose} className="rounded-full bg-slate-100 p-2" data-testid="worker-detail-close"><X className="h-4 w-4 text-slate-600" /></button>
        </div>
        <div className="overflow-y-auto p-5">
          {!d ? (
            <div className="flex justify-center py-10"><Loader2 className="h-6 w-6 animate-spin text-blaze" /></div>
          ) : (
            <>
              {d.has_photo && (
                <img src={`${API}/onboarding/photo/${d.id}`} alt={d.name} className="mx-auto h-24 w-24 rounded-full border-2 border-line object-cover" data-testid="worker-photo" />
              )}
              <p className="mt-3 mb-1 text-[10px] font-bold uppercase tracking-widest text-blaze">Personal</p>
              <Row k="Full Name" v={p.full_name || d.name} />
              <Row k="Role" v={d.worker_role === "driver" ? "Driver" : d.worker_role === "helper" ? "Helper / Offsider" : "—"} />
              <Row k="Email" v={d.email} />
              <Row k="Phone" v={p.phone} />
              <Row k="Address" v={p.address} />
              {d.worker_role === "driver" && (
                <>
                  <p className="mt-4 mb-1 text-[10px] font-bold uppercase tracking-widest text-blaze">Licence</p>
                  <Row k="Licence No." v={p.licence_number} />
                  <Row k="Expiry" v={p.licence_expiry} />
                  <Row k="State / Class" v={`${p.licence_state || "—"} / ${p.licence_class || "—"}`} />
                  <Row k="Demerit History" v={p.demerit_history} />
                </>
              )}
              <p className="mt-4 mb-1 text-[10px] font-bold uppercase tracking-widest text-blaze">Emergency Contact</p>
              <Row k="Name" v={p.emergency_name} />
              <Row k="Relationship" v={p.emergency_relationship} />
              <Row k="Phone" v={p.emergency_phone} />
              <p className="mt-4 mb-1 text-[10px] font-bold uppercase tracking-widest text-blaze">Payroll</p>
              <Row k="Bank" v={p.bank_name} />
              <Row k="Account Holder" v={p.account_holder} />
              <Row k="BSB" v={p.bsb} />
              <Row k="Account No." v={p.account_number} />
              <Row k="ABN" v={p.abn} />
              <Row k="Superannuation" v={p.superannuation} />
              <p className="mt-4 mb-1 text-[10px] font-bold uppercase tracking-widest text-blaze">Agreement</p>
              {d.agreement.accepted ? (
                <>
                  <Row k="Signed Date" v={d.agreement.signed_date} />
                  <Row k="Terms Version" v={d.agreement.terms_version} />
                  {d.agreement.signature_id && (
                    <img src={`${API}/signatures/${d.agreement.signature_id}`} alt="Signature" className="mt-2 max-h-24 rounded-lg border border-line bg-white" data-testid="worker-signature-img" />
                  )}
                  <a
                    href={`${API}/workers/${d.id}/agreement/pdf`}
                    target="_blank"
                    rel="noreferrer"
                    className="mt-4 flex w-full items-center justify-center gap-2 rounded-xl bg-blaze py-3 text-xs font-bold uppercase tracking-widest text-white active:scale-[0.98]"
                    data-testid="download-agreement-pdf-btn"
                  >
                    <FileDown className="h-4 w-4" /> Download Agreement PDF
                  </a>
                </>
              ) : (
                <p className="py-2 text-sm text-red-600" data-testid="worker-not-signed">Agreement not signed yet.</p>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export const WorkersManager = () => {
  const [workers, setWorkers] = useState(null);
  const [detail, setDetail] = useState(null);
  const [busy, setBusy] = useState("");

  const load = () => axios.get(`${API}/workers`).then(({ data }) => setWorkers(data)).catch(() => setWorkers([]));
  useEffect(() => { load(); }, []);

  const setRole = async (w, role) => {
    setBusy(w.id);
    try {
      await axios.put(`${API}/workers/${w.id}/role`, { worker_role: role });
      toast.success(`${w.name} is now a ${role === "driver" ? "Driver" : "Helper"}.`);
      load();
    } catch {
      toast.error("Could not change role.");
    } finally {
      setBusy("");
    }
  };

  const grantGrace = async (w, days) => {
    setBusy(w.id);
    try {
      if (days === "revoke") {
        await axios.post(`${API}/workers/${w.id}/grace`, { revoke: true });
        toast.success(`Temporary access revoked for ${w.name}.`);
      } else {
        await axios.post(`${API}/workers/${w.id}/grace`, { days: Number(days) });
        toast.success(`${w.name} granted access for ${days === 30 || days === "30" ? "1 month" : `${days} days`}.`);
      }
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not update access.");
    } finally {
      setBusy("");
    }
  };

  if (workers === null) return <div className="flex justify-center py-10"><Loader2 className="h-6 w-6 animate-spin text-blaze" /></div>;

  return (
    <div className="mt-10" data-testid="workers-manager">
      <h2 className="font-display text-2xl font-semibold uppercase tracking-tight text-slate-900">Onboarding <span className="text-blaze">&amp; Access</span></h2>
      <p className="mt-1 text-sm text-slate-500">Agreement status, Driver/Helper role and temporary access overrides. Locked workers can't see jobs until they complete onboarding.</p>
      <div className="mt-5 grid items-start gap-3 md:grid-cols-2 xl:grid-cols-3">
        {workers.map((w) => {
          const s = STATUS_UI[w.status] || STATUS_UI.locked;
          return (
            <div key={w.id} className="rounded-2xl border border-slate-200/80 bg-white p-4 shadow-[0_8px_30px_rgb(0,0,0,0.06)] transition-all duration-200 ease-out hover:-translate-y-0.5 hover:shadow-md" data-testid={`worker-card-${w.name.toLowerCase()}`}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  {w.has_photo ? (
                    <img src={`${API}/onboarding/photo/${w.id}`} alt={w.name} className="h-12 w-12 rounded-full border-2 border-white object-cover shadow-sm ring-2 ring-blue-500/20" />
                  ) : (
                    <span className="flex h-12 w-12 items-center justify-center rounded-full bg-blaze font-display text-lg font-bold text-white shadow-sm ring-2 ring-blue-500/20">{w.name.charAt(0).toUpperCase()}</span>
                  )}
                  <div>
                    <p className="font-display text-base font-bold text-slate-900">{w.name}</p>
                    <p className="text-[11px] text-slate-500">{w.worker_role === "driver" ? "Driver" : w.worker_role === "helper" ? "Helper / Offsider" : "Role not set"}</p>
                  </div>
                </div>
                <span className={`flex items-center gap-1 rounded-full px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider ${s.cls}`} data-testid={`worker-status-${w.name.toLowerCase()}`}>
                  <s.Icon className="h-3 w-3" /> {s.label}
                </span>
              </div>
              {w.status === "temporary" && w.grace_until && (
                <p className="mt-2 rounded-lg bg-amber-50 px-3 py-1.5 text-[11px] font-semibold text-amber-700" data-testid={`grace-until-${w.name.toLowerCase()}`}>
                  Access expires {fmtGrace(w.grace_until)}
                </p>
              )}
              {w.agreement_signed && (
                <p className="mt-2 text-[11px] font-semibold text-green-700">Agreement signed {w.signed_date}</p>
              )}
              <div className="mt-3 grid grid-cols-2 gap-2">
                <select
                  value={w.worker_role || ""}
                  onChange={(e) => e.target.value && setRole(w, e.target.value)}
                  disabled={busy === w.id}
                  className="rounded-lg border border-line bg-slate-50 px-2 py-2 text-xs font-semibold text-slate-700 outline-none focus:border-blaze"
                  data-testid={`role-select-${w.name.toLowerCase()}`}
                >
                  <option value="" disabled>Set role…</option>
                  <option value="driver">Driver</option>
                  <option value="helper">Helper</option>
                </select>
                <select
                  value=""
                  onChange={(e) => e.target.value && grantGrace(w, e.target.value)}
                  disabled={busy === w.id || w.status === "active"}
                  className="rounded-lg border border-line bg-slate-50 px-2 py-2 text-xs font-semibold text-slate-700 outline-none focus:border-blaze disabled:opacity-40"
                  data-testid={`grace-select-${w.name.toLowerCase()}`}
                >
                  <option value="" disabled>Grant access…</option>
                  <option value="2">Grant Access: 2 Days</option>
                  <option value="4">Grant Access: 4 Days</option>
                  <option value="30">Grant Access: 1 Month</option>
                  {w.status === "temporary" && <option value="revoke">Revoke Access</option>}
                </select>
              </div>
              <button
                onClick={() => setDetail(w.id)}
                className="mt-2 flex w-full items-center justify-center gap-1.5 rounded-lg border border-blaze py-2 text-[11px] font-bold uppercase tracking-wider text-blaze active:scale-95"
                data-testid={`worker-view-${w.name.toLowerCase()}`}
              >
                <Eye className="h-3.5 w-3.5" /> View Profile &amp; Agreement
              </button>
            </div>
          );
        })}
      </div>
      {detail && <WorkerDetailModal workerId={detail} onClose={() => setDetail(null)} />}
    </div>
  );
};
